import React from 'react'
import { Box, Typography, BoxProps } from '@mui/material'
import { INTERAX_COLORS } from '../../../constants/colors'

export interface GamerFeedbackSummaryTextContainerProps extends BoxProps {
  /**
   * Feedback text content
   */
  children?: React.ReactNode
  /**
   * Title for the text container
   */
  title?: string
  /**
   * Main feedback text
   */
  text?: string
  /**
   * Feedback level (for styling)
   */
  level?: 'focusArea' | 'potential' | 'idea' | 'outstanding' | 'excellent' | 'good' | 'tooMuch' | 'tooLittle'
}

/**
 * GamerFeedbackSummaryTextContainer - Left bottom card for feedback text
 *
 * Features:
 * - Glassmorphism styling
 * - Displays dynamic feedback text from engine
 * - Supports different feedback levels
 * - Flexible content area
 *
 * @example
 * ```tsx
 * <GamerFeedbackSummaryTextContainer
 *   title="Team Performance"
 *   text="Your team has shown strong collaboration skills..."
 *   level="excellent"
 * />
 * ```
 */
export const GamerFeedbackSummaryTextContainer: React.FC<GamerFeedbackSummaryTextContainerProps> = ({
  children,
  title,
  text,
  level,
  sx,
  ...props
}) => {
  const getLevelColor = () => {
    switch (level) {
      case 'outstanding':
      case 'excellent':
        return INTERAX_COLORS.green
      case 'good':
        return INTERAX_COLORS.sun
      case 'focusArea':
        return INTERAX_COLORS.redError
      case 'potential':
      case 'idea':
        return INTERAX_COLORS.blue
      case 'tooMuch':
      case 'tooLittle':
        return INTERAX_COLORS.thunderGrey
      default:
        return INTERAX_COLORS.white
    }
  }

  return (
    <Box
      sx={{
        // Glassmorphism card styling
        background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(6px)',

        // Layout
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        gap: '16px',
        flex: 1,
        minHeight: '346px',
        width: '100%',

        // Custom styles
        ...sx
      }}
      {...props}
    >
      {title &&
        (() => {
          const words = title.trim().split(/\s+/)
          const firstWord = words.length > 1 ? words[0] : ''
          const rest = words.length > 1 ? words.slice(1).join(' ') : words[0] || ''

          return (
            <Box
              sx={{
                boxSizing: 'border-box',
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                margin: 0
              }}
            >
              {/* First word in white (if multiple words) */}
              {firstWord && (
                <Typography
                  sx={{
                    fontFamily: 'Lexend Deca',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '26px',
                    lineHeight: '34px',
                    color: INTERAX_COLORS.white,
                    margin: 0
                  }}
                >
                  {firstWord}
                </Typography>
              )}
              {/* Rest of name in gradient */}
              {rest && (
                <Typography
                  sx={{
                    fontFamily: 'Lexend Deca',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '26px',
                    lineHeight: '34px',
                    background: 'linear-gradient(360deg, #FFD196 -15.91%, #FFAB40 1.14%, #FFFFFF 56.35%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
                    margin: 0
                  }}
                >
                  {rest}
                </Typography>
              )}
            </Box>
          )
        })()}

      {(text || children) && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          {text && (
            <Typography
              sx={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: getLevelColor(),
                margin: 0
              }}
            >
              {text}
            </Typography>
          )}

          {children}
        </Box>
      )}
    </Box>
  )
}

export default GamerFeedbackSummaryTextContainer
